<template>
  <auth-layout>
    <div v-if="notifications.length" class="login-page__notifications">
      <div v-for="n in notifications" :key="n.id" class="pop-auth__box pop-auth__box--info" style="margin-bottom: 10px">
        <b v-if="n.title">{{ n.title }}. </b><span v-html="n.message"></span>
      </div>
    </div>

    <h1 class="pop-auth__h1">Welcome back</h1>
    <p class="pop-auth__sub">Log in to your POP account.</p>

    <div v-if="secureExpired" class="pop-auth__box pop-auth__box--info" style="margin-bottom: 18px">
      That security link expired. Log in, or
      <router-link class="pop-auth__link" :to="{ path: '/forgotpassword', query: { email } }">reset your password</router-link>.
    </div>
    <div v-if="globalErrorMsg" class="pop-auth__box pop-auth__box--danger" style="margin-bottom: 18px">{{ globalErrorMsg }}</div>

    <social-buttons :busy="socialBusy" @google="onGoogle" @apple="onApple" />

    <form novalidate @submit.prevent="submit">
      <label class="pop-auth__label" for="login-email">Email</label>
      <input
        id="login-email"
        v-model.trim="email"
        class="pop-auth__field"
        :class="{ 'is-invalid': Boolean(error) }"
        type="email"
        placeholder="you@company.com"
        autocomplete="email"
        :disabled="locked"
        data-test="login-email"
        @input="error = ''"
      />

      <div class="login-page__password-row">
        <label class="pop-auth__label" for="login-password">Password</label>
        <router-link class="pop-auth__link login-page__forgot" :to="{ path: '/forgotpassword', query: email ? { email } : {} }">Forgot password?</router-link>
      </div>
      <password-field
        v-model="password"
        input-id="login-password"
        placeholder="Your password"
        autocomplete="current-password"
        :error="error"
        :disabled="locked"
        data-test="login-password"
        error-data-test="login-error"
        @input="error = ''"
        @enter="submit"
      />

      <div v-if="locked" class="pop-auth__box pop-auth__box--danger" data-test="login-locked">
        Too many attempts. Log in is paused for {{ lockClock }} for this account and device. You can still
        <router-link class="pop-auth__link" :to="{ path: '/forgotpassword', query: { email } }">reset your password</router-link>.
      </div>
      <div v-if="blocked" class="pop-auth__box pop-auth__box--danger">
        <b>Account suspended.</b> Your account has been temporarily suspended due to suspicious activity.
        <a class="pop-auth__link" href="mailto:support@pageoptimizer.pro">Contact support</a> to reactivate it.
      </div>

      <turnstile-widget ref="captcha" action="login" @token="captchaToken = $event" @expired="captchaToken = ''" />
      <button type="submit" class="pop-auth__btn pop-auth__btn--primary" :disabled="busy || locked || captchaPending" data-test="login-button">
        {{ busy ? 'Logging in' : 'Log in' }}
      </button>
    </form>

    <p class="pop-auth__fine pop-auth__center" style="margin-top: 20px">
      New to POP? <router-link class="pop-auth__link" to="/register">Create an account</router-link>
    </p>

  </auth-layout>
</template>

<script>
import AuthLayout from '../AuthLayout.vue'
import PasswordField from '../PasswordField.vue'
import TurnstileWidget from '../TurnstileWidget.vue'
import SocialButtons from '../SocialButtons.vue'
import { exchangeSocialCode, providerLabel } from '../social'
import { finishLogin } from '../session'
import { isTurnstileEnabled } from '@/utils/turnstile'
import { getDeviceId } from '@/utils/device-id'
import { getToken, segmentPage } from '@/utils'
import { DEMO_SESSION_KEYS } from '@/components/PageRun/constants'

export default {
  name: 'LoginPage',
  components: { AuthLayout, PasswordField, SocialButtons, TurnstileWidget },
  data() {
    return {
      socialBusy: false,
      captchaToken: '',
      email: '',
      password: '',
      busy: false,
      error: '',
      blocked: false,
      lockedUntil: 0,
      now: Date.now(),
      ticker: null,
      notifications: [],
      globalErrorMsg: '',
      secureExpired: false,
    }
  },
  computed: {
    locked() {
      return this.lockedUntil > this.now
    },
    lockClock() {
      const total = Math.max(0, Math.ceil((this.lockedUntil - this.now) / 1000))
      return total >= 60 ? `${Math.ceil(total / 60)} minutes` : `${total} seconds`
    },
    redirect() {
      return this.$route.query.redirect || ''
    },
    captchaPending() {
      return isTurnstileEnabled() && !this.captchaToken
    },
  },
  beforeMount() {
    this.globalErrorMsg = this.$store.state.globalErrorMsg || ''
  },
  mounted() {
    Object.values(DEMO_SESSION_KEYS).forEach((key) => sessionStorage.removeItem(key))
    if (getToken()) {
      // Already logged in, typically because another tab just logged in and the cross-tab reload
      // brought this one here. Clearing the store would remove that token for every tab, so go to
      // the dashboard instead (2026-10-21: login followed by a logout a few seconds later).
      this.$router.replace(this.redirect || '/sites')
      return
    }
    this.$store.commit('clearStore')
    this.email = String(this.$route.query.email || '')
    this.secureExpired = this.$route.query.secure === 'expired'
    this.ticker = setInterval(() => { this.now = Date.now() }, 1000)
    segmentPage('Login', {})
    this.$store.dispatch('getLoginPageNotifications')
      .then((response) => {
        if (response.data && response.data.status === 'SUCCESS') this.notifications = response.data.notifications || []
      })
      .catch((e) => console.log(e))
  },
  beforeDestroy() {
    clearInterval(this.ticker)
  },
  methods: {
    onGoogle(code) {
      return this.social({ provider: 'google', code })
    },
    onApple({ code, name, nonce }) {
      return this.social({ provider: 'apple', code, name, nonce })
    },
    async social(credential) {
      this.socialBusy = true
      this.globalErrorMsg = ''
      try {
        this.globalErrorMsg = await exchangeSocialCode(this, { ...credential, intent: 'login', redirect: this.redirect })
      } catch (e) {
        console.log(e)
        this.globalErrorMsg = `${providerLabel(credential.provider)} sign-in didn't complete. Please try again.`
      } finally {
        this.socialBusy = false
      }
    },
    async submit() {
      if (this.busy || this.locked) return
      this.error = ''
      this.blocked = false
      if (!this.email || !this.password) {
        this.error = 'Enter your email and password.'
        return
      }
      this.busy = true
      try {
        const captchaToken = this.captchaToken
        const response = await this.$store.dispatch('loginWithPassword', {
          email: this.email, password: this.password, captchaToken, deviceId: getDeviceId(),
        })
        this.handle(response.data || {})
      } catch (e) {
        console.log(e)
        this.error = 'Something went wrong. Please try again.'
      } finally {
        this.busy = false
        if (this.$refs.captcha) this.$refs.captcha.reset() // tokens are single-use
      }
    },
    handle(data) {
      switch (data.action) {
        case 'LOGIN':
          finishLogin(this, { token: data.token, user: data.user }, { event: 'login', redirect: this.redirect })
          break
        case 'LOGIN_CODE_NEEDED':
          this.$store.commit('signup/patch', {
            email: data.email || this.email,
            loginChallenge: {
              pendingLoginToken: data.pendingLoginToken,
              email: data.email || this.email,
              redirect: this.redirect,
              resendAvailableAt: Date.now() + (data.resendAfterSeconds || 45) * 1000,
              lockedUntil: 0,
            },
          })
          this.$router.push('/login/code')
          break
        case 'EMAIL_NOT_VERIFIED':
          this.$store.commit('signup/patch', {
            email: data.email || this.email, provider: 'password', pendingToken: data.pendingToken,
            resendAvailableAt: Date.now() + 45 * 1000, lockedUntil: 0, entry: 'direct',
          })
          this.$router.push('/register/verify')
          break
        case 'INVALID_CREDENTIALS': {
          const left = data.attemptsLeft
          this.error = left != null
            ? `Incorrect email or password. ${left} ${left === 1 ? 'attempt' : 'attempts'} left.`
            : 'Incorrect email or password.'
          break
        }
        case 'LOCKED':
          this.lockedUntil = Date.now() + (data.retryAfterSeconds || 900) * 1000
          break
        case 'ADMIN_BLOCK':
          this.blocked = true
          break
        default:
          this.error = data.msg || 'Something went wrong. Please try again.'
      }
    },
  },
}
</script>

<style scoped lang="scss">
.login-page__password-row { display: flex; justify-content: space-between; align-items: baseline; margin-top: 16px; }
.login-page__forgot { font-size: 12.5px; }
.login-page__notifications { margin-bottom: 18px; }
</style>
